import { Children, FC, isValidElement, ReactElement, ReactNode } from "react";

import { CardLayout } from "./CardLayout";
import { Button } from "./catalyst/Button";
import { ProgressBar, ProgressBarItem, ProgressBarProps } from "./ProgressBar";

type StepProps = {
  children: ReactNode;
  nextLabel: string;
  onNext: () => void;
  onPrev?: () => void;
  title: string;
};

export const Step: FC<StepProps> = ({ children }) => {
  return <div>{children}</div>;
};

type MultiStepProps = {
  activeStep: number;
  buttonsDisabled: boolean;
  children: ReactNode;
  progressHeader?: ReactNode;
  progressIndicator?: FC<ProgressBarProps>;
  setActiveStep: (step: number) => void;
  showButtons?: boolean; // New prop to control button visibility
};

// New component for rendering navigation buttons separately
type MultiStepNavigationProps = {
  activeStep: number;
  buttonsDisabled: boolean;
  children: ReactNode;
  onNext?: () => void;
  onPrev?: () => void;
  setActiveStep: (step: number) => void;
  totalSteps: number;
};

export const MultiStepNavigation: FC<MultiStepNavigationProps> = ({
  activeStep,
  buttonsDisabled,
  children,
  onNext,
  onPrev,
  setActiveStep,
  totalSteps,
}) => {
  const steps = Children.toArray(children).filter(
    (child) => isValidElement(child) && child.type === Step,
  ) as ReactElement<StepProps>[];

  const currentStep = steps[activeStep];

  const handleNext = (): void => {
    if (onNext) {
      onNext();
    } else if (activeStep < totalSteps - 1) {
      setActiveStep(activeStep + 1);
    }
  };

  const handlePrev = (): void => {
    if (onPrev) {
      onPrev();
    } else if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  return (
    <>
      {activeStep > 0 && (
        <Button disabled={buttonsDisabled} onClick={handlePrev} outline>
          Previous
        </Button>
      )}
      <Button
        color="emerald"
        disabled={buttonsDisabled}
        onClick={handleNext}
        type="submit"
      >
        {currentStep?.props.nextLabel || "Next"}
      </Button>
    </>
  );
};

export const MultiStepWizard = ({
  activeStep,
  buttonsDisabled,
  children,
  progressHeader,
  progressIndicator,
  setActiveStep,
  showButtons = true, // Default to true for backward compatibility
}: MultiStepProps): ReactElement => {
  const steps = Children.toArray(children).filter(
    (child) => isValidElement(child) && child.type === Step,
  ) as ReactElement<StepProps>[];

  const progressSteps: ProgressBarItem[] = steps.map((step, index) => ({
    href: "#",
    id: (index + 1).toString().padStart(2, "0"),
    name: isValidElement(step) ? step.props.title : `Step ${index + 1}`,
    status:
      index < activeStep
        ? "complete"
        : index === activeStep
          ? "current"
          : "upcoming",
  }));

  const currentStep = steps[activeStep];

  const ProgressIndicatorComponent = progressIndicator || ProgressBar;

  return (
    <>
      <CardLayout header={progressHeader} highlightHeader={true} margin={false}>
        <ProgressIndicatorComponent steps={progressSteps} />
      </CardLayout>
      {currentStep}
      {showButtons && (
        <div className="mt-2 sm:flex sm:items-start sm:justify-between">
          <div className="max-w-xl text-sm text-gray-500">
            {activeStep > 0 && (
              <Button
                disabled={buttonsDisabled}
                onClick={() => {
                  setActiveStep(activeStep - 1);
                }}
                outline
              >
                Previous
              </Button>
            )}
          </div>
          <div className="mt-5 sm:ml-6 sm:mt-0 sm:flex sm:shrink-0 sm:items-center">
            <Button color="emerald" disabled={buttonsDisabled} type="submit">
              {currentStep.props.nextLabel}
            </Button>
          </div>
        </div>
      )}
    </>
  );
};
